/* v4.2.2 sticky-atc layout/controls; adapted to Dawn's single product form and section refresh. */
(() => {
  if (customElements.get('db-sticky')) return;
  class DawnSticky extends HTMLElement {
    connectedCallback() {
      if (this.controller && !this.controller.signal.aborted) return;
      this.info = this.closest('product-info');
      if (!this.info) return;
      this.controller = new AbortController();
      this.anchor = document.createComment('db-sticky source');
      this.before(this.anchor);
      this.button = this.querySelector('[data-sticky-submit]');
      this.status = this.querySelector('[data-sticky-status]');
      this.label = this.querySelector('.sticky-atc__label__text');
      try { this.variants = JSON.parse(this.querySelector('[data-sticky-variants]')?.textContent || '[]'); }
      catch { this.variants = []; }
      // A transformed product-info ancestor traps position:fixed. Keep ownership at
      // the source anchor while rendering the original full-width bar under body.
      document.body.append(this);
      this.on(this.button, 'click', () => this.activate());
      this.on(this, 'change', event => {
        if (event.target.matches('select')) this.selectVariant(event.target);
      });
      this.on(window, 'scroll', () => this.refresh());
      this.on(window, 'resize', () => this.refresh());
      this.on(document, 'shopify:block:select', event => {
        if (event.detail?.blockId === this.dataset.blockId) { this.editorSelected = true; this.refresh(); }
      });
      this.on(document, 'shopify:block:deselect', event => {
        if (event.detail?.blockId === this.dataset.blockId) { this.editorSelected = false; this.refresh(); }
      });
      this.on(document, 'input', event => {
        if (event.target.form === this.form) { this.validationError = false; this.write(this.status, ''); this.refresh(); }
      });
      this.on(document, 'change', event => {
        if (!this.contains(event.target) && this.info.contains(event.target)) this.refresh();
      });
      document.addEventListener('invalid', event => {
        if (event.target.form !== this.form) return;
        const field = event.target.closest('v4-custom-product-field');
        if (field && field.dataset.applyStickyAtcError !== 'true') return;
        this.validationError = true;
        this.write(this.status, event.target.validationMessage);
      }, { capture: true, signal: this.controller.signal });
      this.on(this.info, 'product-info:request-error', () => {
        this.pendingVariant = null;
        this.write(this.status, 'Unable to update this option. Please select it again.');
        this.refresh();
      });
      this.on(this.info, 'product-info:updated', () => { this.pendingVariant = null; this.refresh(); });
      this.on(this.info, 'subscription-price:change', event => {
        if (event.target.dataset.updatePrices === 'false') return;
        const { price, comparePrice } = event.detail || {};
        if (Number.isFinite(price)) {
          this.planPrice = { price, comparePrice, variantId: this.form?.querySelector('[name="id"]')?.value };
          this.syncPrice(price, comparePrice);
        }
      });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          const data = event?.data;
          if (String(data?.sectionId) !== String(this.info.dataset.section)) return;
          this.pendingVariant = null;
          this.validationError = false;
          this.write(this.status, '');
          this.refresh();
        });
      }
      this.observer = new MutationObserver(records => {
        if (!this.anchor.isConnected || !this.info.isConnected) { this.remove(); return; }
        if (records.some(r => !this.contains(r.target) &&
          (this.info.contains(r.target) || r.target.matches?.('cart-drawer,product-modal,modal-dialog,dialog') ||
            [...r.addedNodes, ...r.removedNodes].some(n => n.matches?.('cart-drawer,product-modal,modal-dialog,dialog'))))) this.refresh();
      });
      this.observer.observe(document.body, { subtree: true, childList: true, attributes: true, characterData: true });
      this.refresh();
    }
    on(target, event, callback) { target?.addEventListener(event, callback, { signal: this.controller.signal }); }
    write(node, value) { if (node && node.textContent !== String(value)) node.textContent = String(value); }
    get form() { return document.getElementById(this.dataset.form); }
    get main() { return this.form?.querySelector('[name="add"][type="submit"]'); }
    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.dataset.currency || 'USD',
      }).format(Number(cents || 0) / 100);
    }
    syncPrice(price, comparePrice) {
      const root = this.querySelector('.sticky-atc__price .price');
      const sale = Number(comparePrice) > price;
      root?.classList.toggle('price--on-sale', sale);
      root?.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale')
        .forEach(node => this.write(node, this.money(price)));
      root?.querySelectorAll('.price__sale .price-item--regular')
        .forEach(node => this.write(node, this.money(comparePrice || price)));
      this.querySelectorAll('.sticky-atc__button-price .main-atc-price-span-' + CSS.escape(this.info.dataset.section))
        .forEach(node => this.write(node, this.money(price)));
      const scrollPrice = this.querySelector('.sticky-atc__scroll-btn [id^="sticky-atc-price-"]');
      if (scrollPrice) this.write(scrollPrice, this.money(price) + ' •');
    }
    refresh() {
      if (!this.info?.isConnected || !this.button) return;
      const form = this.form, main = this.main;
      const scroll = this.dataset.function !== 'add_to_cart';
      const busy = !!this.pendingVariant || main?.classList.contains('loading');
      this.button.disabled = scroll ? !this.scrollTarget() : !main || main.disabled || main.getAttribute('aria-disabled') === 'true' || !!this.pendingVariant;
      this.button.setAttribute('aria-busy', String(!!busy));
      this.write(this.label, this.pendingVariant ? 'Updating…' : busy ? 'Adding…' : !scroll && main?.disabled ? main.textContent.trim() : this.dataset.label || 'Add to cart');
      const currentId = form?.querySelector('[name="id"]')?.value;
      const variant = this.variants.find(v => String(v.id) === String(currentId));
      if (this.planPrice && String(this.planPrice.variantId) !== String(currentId)) this.planPrice = null;
      if (variant && !this.pendingVariant) {
        this.syncPickers(variant);
        this.syncPrice(this.planPrice?.price ?? variant.price, this.planPrice?.comparePrice ?? variant.compare_at_price);
        const sourcePrice = this.info.querySelector('[id^="price-"] .price');
        const regular = sourcePrice?.querySelector('.price__regular .price-item--regular');
        const sale = sourcePrice?.querySelector('.price__sale .price-item--sale');
        const activePrice = sourcePrice?.classList.contains('price--on-sale') ? sale : regular;
        // Main price may already include the active selling plan; do not replace it
        // with base-variant text when quantity or another control changes.
        if (sourcePrice) {
          const stickyPrice = this.querySelector('.sticky-atc__price .price');
          if (stickyPrice) {
            stickyPrice.classList.toggle('price--on-sale', sourcePrice.classList.contains('price--on-sale'));
            for (const selector of ['.price__regular .price-item--regular', '.price__sale .price-item--sale', '.price__sale .price-item--regular']) {
              const from = sourcePrice.querySelector(selector), to = stickyPrice.querySelector(selector);
              if (from && to) this.write(to, from.textContent.trim());
            }
          }
          if (activePrice) this.querySelectorAll('.sticky-atc__button-price [class^="main-atc-price-span-"]')
            .forEach(node => this.write(node, activePrice.textContent.trim()));
          const scrollPrice = this.querySelector('.sticky-atc__scroll-btn [id^="sticky-atc-price-"]');
          if (activePrice && scrollPrice) this.write(scrollPrice, activePrice.textContent.trim() + ' •');
        }
        const image = this.querySelector('.sticky-atc__image img');
        const src = variant.featured_image?.src || this.dataset.productImage;
        if (image && src && image.getAttribute('src') !== src) image.setAttribute('src', src);
      }
      const error = form?.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      if (error && !error.hidden) this.write(this.status, error.querySelector('.product-form__error-message')?.textContent.trim() || '');
      const modal = document.querySelector('cart-drawer.active,product-modal[open],modal-dialog[open],dialog[open]');
      const preview = window.Shopify?.designMode && (this.dataset.preview === 'true' || this.editorSelected);
      const after = main && main.getBoundingClientRect().bottom <= 0;
      const visible = !modal && (preview || (scroll || main) && (this.dataset.afterScroll !== 'true' || after));
      this.hidden = !visible;
      this.classList.toggle('active', !!visible);
      this.setAttribute('aria-hidden', String(!visible));
    }
    syncPickers(variant) {
      const combined = this.querySelector('.sticky-atc__variant-select');
      if (combined) combined.value = String(variant.id);
      else this.querySelectorAll('[id^="StickyAtcVariantPicker-"] select').forEach((select, index) => {
        select.value = variant.options[index];
        for (const option of select.options) {
          option.disabled = !this.variants.some(v => v.available && v.options[index] === option.value &&
            v.options.slice(0, index).every((value, n) => value === variant.options[n]));
          const label = option.value + (option.disabled ? ' — ' + (window.variantStrings?.unavailable || 'Unavailable') : '');
          if (option.textContent !== label) option.textContent = label;
        }
      });
    }
    selectVariant(control) {
      const picker = control.closest('[id^="StickyAtcVariantPicker-"]');
      if (!picker) return;
      const combined = picker.querySelector('.sticky-atc__variant-select');
      const selects = Array.from(picker.querySelectorAll('select'));
      const values = selects.map(s => s.value);
      let variant = combined ? this.variants.find(v => String(v.id) === combined.value)
        : this.variants.find(v => v.options.every((value, i) => value === values[i]));
      if (!combined && (!variant || !variant.available)) {
        const index = selects.indexOf(control);
        variant = this.variants.find(v => v.available && v.options.slice(0, index + 1).every((value, i) => value === values[i]));
      }
      if (!variant || !variant.available) { this.write(this.status, 'This option is unavailable.'); return; }
      if (String(variant.id) === String(this.form?.querySelector('[name="id"]')?.value) && !this.pendingVariant) return;
      this.pendingVariant = String(variant.id);
      this.write(this.status, '');
      this.refresh();
      const mainPicker = this.info.querySelector('variant-selects');
      let changed = null;
      const groups = mainPicker ? Array.from(mainPicker.querySelectorAll('fieldset, .product-form__input--dropdown')) : [];
      let matched = groups.length === variant.options.length;
      groups.forEach((group, i) => {
        const value = variant.options[i];
        const select = group.querySelector('select');
        if (select) {
          const option = Array.from(select.options).find(o => o.value === value);
          if (!option) { matched = false; return; }
          select.value = option.value;
          Array.from(select.options).forEach(o => o.toggleAttribute('selected', o === option));
          changed = select;
        } else {
          const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(o => o.value === value);
          if (!radio) { matched = false; return; }
          radio.checked = true; changed = radio;
        }
      });
      if (matched && changed) {
        changed.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (typeof this.info.renderProductInfo === 'function') {
        this.info.resetProductFormState();
        this.info.querySelectorAll('dawn-purchase-options').forEach(w => w.setPending?.());
        const url = new URL(this.info.dataset.url, location.origin);
        url.searchParams.set('variant', variant.id); url.searchParams.set('section_id', this.info.dataset.section);
        this.info.renderProductInfo({ requestUrl: url.toString(), targetId: control.id || this.id,
          callback: this.info.handleUpdateProductInfo(this.info.dataset.url) });
      } else {
        this.pendingVariant = null;
        this.write(this.status, 'Product options are not ready. Please try again.');
        this.refresh();
      }
    }
    scrollTarget() {
      switch (this.dataset.function) {
        case 'scroll_#ProductInfo-id': return this.info.querySelector('[id^="ProductInfo-"]') || this.info;
        case 'scroll_#variant-selects-id': return this.info.querySelector('variant-selects');
        case 'scroll_#quantity-breaks-id': return this.info.querySelector('v6-quantity-breaks, .quantity-breaks, db-quantity');
        case 'scroll_.pricing-table': return this.info.querySelector('.pricing-table') || document.querySelector('.pricing-table');
        default: return null;
      }
    }
    activate() {
      if (this.dataset.function !== 'add_to_cart') {
        this.scrollTarget()?.scrollIntoView({ behavior: 'smooth', block: 'center' }); return;
      }
      const form = this.form, main = this.main;
      if (!form || !main || main.disabled || main.getAttribute('aria-disabled') === 'true' || this.pendingVariant) return;
      this.validationError = false;
      this.write(this.status, '');
      if (!form.reportValidity()) return;
      form.requestSubmit(main);
      this.refresh();
    }
    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.controller?.abort(); this.observer?.disconnect(); this.unsubscribe?.();
        this.anchor?.remove();
      });
    }
  }
  customElements.define('db-sticky', DawnSticky);
})();
